import React from 'react';
import { Calendar, MapPin, Users, Clock, Award } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const AI4HERProgramPage: React.FC = () => {
  const speakers = [
    {
      name: "Dr. Sue Keay",
      title: "Director, UNSW AI Institute | President AI & Robotics Australia"
    },
    {
      name: "Prof. Lihong",
      title: "Director, School of Computer Engineering & Maths, Charles Sturt University"
    },
    {
      name: "Dimitry Tran",
      title: "Harrison.ai Australia"
    },
    {
      name: "Sebastian Sequoiah-Grayson",
      title: "Senior Lecturer, UNSW"
    },
    {
      name: "Dr Sharifah Sakinah",
      title: "Responsible AI Lead Professor, University of Technology of Malaysia (UTeM)"
    },
    {
      name: "Chloe Zheng",
      title: "Head of Partnerships, MBZUAI UAE"
    }
  ];

  const schedule = [
    {
      time: "09:00",
      type: "Registration",
      title: "Registration & Morning Networking Tea",
      description: "Participant arrival and networking • Meet the speakers and partners",
      isKeynote: false
    },
    {
      time: "09:25",
      type: "Welcome",
      title: "Welcome Remarks",
      speaker: "Prof. Byong Gul Lee",
      speakerTitle: "Dean of College of Future Industry Convergence, Seoul Women's University",
      isKeynote: false
    },
    {
      time: "09:35",
      type: "Opening",
      title: "Opening Remarks",
      speaker: "Angela Kim",
      speakerTitle: "Head of Data Science & AI, AIA Australia; President, AI4APAC; Women in AI APAC",
      isKeynote: false
    },
    {
      time: "09:45",
      type: "Opening Keynote",
      title: "APAC as the Engine Room for AI Talent — Unlocking Inclusive Opportunities for Women",
      speaker: "Dr. Sue Keay",
      speakerTitle: "Director, UNSW AI Institute | President AI & Robotics Australia",
      isKeynote: true,
      keyPoints: [
        "APAC is a global growth hub for AI talent",
        "Women's pathways into AI are diverse and multidisciplinary",
        "Responsible AI and sustainability are inseparable from workforce strategy",
        "Building the AI workforce depends on inclusion, enabling women to lead",
        "Talent development must balance deep expertise, adjacent capability, and broad AI literacy"
      ]
    },
    {
      time: "10:10",
      type: "Keynote",
      title: "AI for Agriculture & Smart Farm",
      speaker: "Prof. Lihong",
      speakerTitle: "Director, School of Computer Engineering & Maths, Charles Sturt University | Digital Twin (Via Zoom)",
      isKeynote: true
    },
    {
      time: "10:30",
      type: "Keynote",
      title: "AI to Address Healthcare Inequity",
      speaker: "Dimitry Tran",
      speakerTitle: "Harrison.ai Australia",
      isKeynote: true,
      keyPoints: [
        "AI can close access gaps in healthcare",
        "Equity must be designed into AI systems",
        "Demographics, Inequality, and Epistemic Deficits in the Asian Century"
      ]
    },
    {
      time: "10:50",
      type: "Break",
      title: "Networking Break",
      description: "Coffee, tea, and light refreshments",
      isKeynote: false,
      isBreak: true
    },
    {
      time: "11:20",
      type: "Keynote",
      title: "Digital Breakthroughs and Civic Attitudes: Anticipating Behaviour in the Age of AI",
      speaker: "Sebastian Sequoiah-Grayson",
      speakerTitle: "Senior Lecturer, UNSW",
      isKeynote: true,
      keyPoints: [
        "AI as a New Source of Justification and Authority",
        "Demographics, Inequality, and Epistemic Deficits in the Asian Century",
        "Limits of AI Literacy and the Role of Digital Democracy",
        "AI will shape how we justify decisions, not just what we decide",
        "Strong civic agency must come before AI capability"
      ]
    },
    {
      time: "11:50",
      type: "Interactive Panel",
      title: "AI & Innovation – From Ethics to Impact",
      moderator: "Angela Kim – AI4APAC President",
      panelists: [
        "Dr. Sue Keay – Director, UNSW AI Institute | President AI & Robotics Australia",
        "Sebastian Sequoiah-Grayson – Senior Lecturer, UNSW",
        "Dr Sharifah Sakinah – Responsible AI Lead Professor, University of Technology of Malaysia (UTeM)"
      ],
      themes: [
        "Responsible & Human-Centred AI",
        "AI for Social Good",
        "Empowering Women & Youth",
        "Global Collaboration"
      ],
      isKeynote: true
    },
    {
      time: "12:30",
      type: "Special Session",
      title: "Global Collaboration in AI Education",
      speaker: "Chloe Zheng",
      speakerTitle: "Head of Partnerships, MBZUAI UAE",
      isKeynote: false
    },
    {
      time: "12:45",
      type: "Closing",
      title: "Closing Remarks & Group Photo",
      speaker: "Angela Kim",
      speakerTitle: "AI4APAC Founder, Women in AI APAC President",
      isKeynote: false
    },
    {
      time: "13:00",
      type: "Networking",
      title: "Networking Lunch & Informal Discussions",
      description: "Continue conversations with speakers and fellow attendees",
      isKeynote: false,
      isBreak: true
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-navy-50 to-teal-50">
      <Header />
      <div className="h-16" />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-navy-900 via-navy-900 to-teal-900 text-white py-20 overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PHBhdGggZD0iTTM2IDM0di00aC0ydjRoLTR2Mmg0djRoMnYtNGg0di0yaC00em0wLTMwVjBoLTJ2NGgtNHYyaDR2NGgyVjZoNFY0aC00ek02IDM0di00SDR2NEgwdjJoNHY0aDJ2LTRoNHYtMkg2ek02IDRWMEG0djRIMHYyaDR2NGgyVjZoNFY0SDZ6Ii8+PC9nPjwvZz48L3N2Zz4=')] opacity-20"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="inline-block bg-pink-500/20 border border-pink-300 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <span className="text-pink-200">🌸 Special Event</span>
          </div>

          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            <span className="bg-gradient-to-r from-pink-300 to-purple-200 bg-clip-text text-transparent">
              AI4HER
            </span>
            <br />
            Women in AI APAC WaiTALK
          </h1>

          <p className="text-2xl text-purple-100 italic mb-12 font-light">
            AI and Innovation for a Better Future
          </p>

          {/* Event Info Grid */}
          <div className="grid md:grid-cols-2 gap-6 max-w-3xl">
            <div className="flex items-start space-x-4 bg-white/10 backdrop-blur-sm rounded-lg p-4">
              <Calendar className="h-8 w-8 text-pink-300 flex-shrink-0 mt-1" />
              <div>
                <div className="text-xs uppercase text-purple-200 tracking-wider mb-1">Date & Time</div>
                <div className="text-lg font-medium">December 19, 2025</div>
                <div className="text-yellow-300">9:00 AM – 1:00 PM</div>
              </div>
            </div>

            <div className="flex items-start space-x-4 bg-white/10 backdrop-blur-sm rounded-lg p-4">
              <MapPin className="h-8 w-8 text-pink-300 flex-shrink-0 mt-1" />
              <div>
                <div className="text-xs uppercase text-purple-200 tracking-wider mb-1">Venue</div>
                <div className="text-lg font-medium">서울여대 50주년기념관</div>
                <div className="text-yellow-300">Level 6, Room 640</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Hosted By */}
      <section className="py-8 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="text-sm uppercase text-gray-500 tracking-wider mb-4">Hosted By</div>
          <div className="flex flex-wrap justify-center items-center gap-4 text-lg font-semibold text-gray-800">
            <span>AI4APAC</span>
            <span className="text-pink-400">&</span>
            <span>Women in AI APAC</span>
            <span className="text-pink-400">&</span>
            <span>서울여대 인간AI 협력연구센터</span>
          </div>
        </div>
      </section>

      {/* Program Schedule */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 flex items-center">
            <div className="w-1 h-12 bg-gradient-to-b from-pink-400 to-yellow-400 rounded mr-4"></div>
            Program Schedule
          </h2>

          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-20 top-0 bottom-0 w-0.5 bg-gradient-to-b from-pink-300 via-yellow-300 to-pink-300 hidden md:block"></div>

            <div className="space-y-8">
              {schedule.map((session, index) => (
                <div key={index} className="relative md:pl-32">
                  {/* Time */}
                  <div className="md:absolute md:left-0 md:top-0 md:w-20 md:text-right font-serif text-lg font-semibold text-pink-600 mb-2 md:mb-0">
                    {session.time}
                  </div>

                  {/* Timeline marker */}
                  <div className={`hidden md:block absolute left-[4.6rem] top-1 w-5 h-5 rounded-full border-3 bg-white shadow-lg ${
                    session.isKeynote ? 'border-yellow-400 shadow-yellow-200' : 'border-pink-300 shadow-pink-200'
                  }`}></div>

                  {/* Content Card */}
                  <div className={`bg-white rounded-lg p-6 shadow-md hover:shadow-lg transition-shadow border-l-4 ${
                    session.isKeynote ? 'border-yellow-400 bg-gradient-to-br from-white to-yellow-50' :
                    session.isBreak ? 'border-gray-400 bg-gray-50' : 'border-pink-300'
                  }`}>
                    <div className={`inline-block px-3 py-1 rounded text-xs font-semibold uppercase tracking-wide mb-3 ${
                      session.isKeynote ? 'bg-yellow-400 text-gray-900' :
                      session.type === 'Interactive Panel' ? 'bg-pink-600 text-white' :
                      session.isBreak ? 'bg-gray-400 text-white' : 'bg-pink-400 text-white'
                    }`}>
                      {session.type}
                    </div>

                    <h3 className="text-xl font-bold text-gray-900 mb-3">{session.title}</h3>

                    {session.speaker && (
                      <div className="flex items-start space-x-2 mb-2">
                        <Users className="h-5 w-5 text-pink-500 mt-0.5" />
                        <div>
                          <span className="font-semibold text-pink-600">{session.speaker}</span>
                          {session.speakerTitle && (
                            <span className="text-gray-600"> – {session.speakerTitle}</span>
                          )}
                        </div>
                      </div>
                    )}

                    {session.moderator && (
                      <div className="flex items-start space-x-2 mb-3">
                        <Users className="h-5 w-5 text-pink-500 mt-0.5" />
                        <div>
                          <span className="font-semibold">Moderator:</span>{' '}
                          <span className="text-pink-600">{session.moderator}</span>
                        </div>
                      </div>
                    )}

                    {session.panelists && (
                      <div className="bg-pink-50 rounded-lg p-4 mt-4">
                        <div className="text-sm font-semibold uppercase text-gray-700 mb-2 tracking-wide">Panelists</div>
                        <div className="space-y-1">
                          {session.panelists.map((panelist, idx) => (
                            <div key={idx} className="text-sm text-gray-700 pl-3 relative before:content-['•'] before:absolute before:left-0 before:text-pink-400 before:font-bold">
                              {panelist}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {session.themes && (
                      <div className="flex flex-wrap gap-2 mt-4">
                        {session.themes.map((theme, idx) => (
                          <span key={idx} className="px-3 py-1 bg-white border border-pink-300 rounded text-sm text-gray-700">
                            {theme}
                          </span>
                        ))}
                      </div>
                    )}

                    {session.description && (
                      <p className="text-gray-600 mt-3">{session.description}</p>
                    )}

                    {session.keyPoints && (
                      <ul className="mt-4 space-y-2">
                        {session.keyPoints.map((point, idx) => (
                          <li key={idx} className="text-sm text-gray-700 pl-5 relative before:content-['→'] before:absolute before:left-0 before:text-pink-400 before:font-bold">
                            {point}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Featured Speakers */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-gray-900 mb-4 text-center">Featured Speakers</h2>
          <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
            Learn from leading experts in AI, innovation, and technology from across the APAC region
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {speakers.map((speaker, index) => (
              <div key={index} className="bg-gradient-to-br from-teal-50 to-purple-50 rounded-lg p-6 border border-pink-200 hover:shadow-lg transition-shadow">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{speaker.name}</h3>
                <p className="text-sm text-gray-600">{speaker.title}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-pink-600 to-navy-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">🌸 Join Us for an Inspiring Day</h2>
          <p className="text-xl mb-8 text-purple-100">
            Connect with global AI leaders, explore innovative ideas, and be part of shaping the future of AI in the APAC region.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AI4HERProgramPage;
